import { useState } from 'react';
import { findClosestMarker } from '../utils/color';

export function useColorPicker() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [pickedColors, setPickedColors] = useState([]);
  const [isPickingMode, setIsPickingMode] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [hoveredColor, setHoveredColor] = useState(null);

  // Start color picking process
  const startPicking = (imageFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        file: imageFile,
        dataUrl: e.target.result
      });
      setIsPickingMode(true);
      setPickedColors([]);
    };
    reader.readAsDataURL(imageFile);
  };

  // Pick a color at specific coordinates
  const pickColorAt = async (canvas, x, y, availableMarkers) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;
    
    // Convert RGB to hex
    const hex = `#${[r, g, b].map(x => {
      const hexValue = x.toString(16);
      return hexValue.length === 1 ? '0' + hexValue : hexValue;
    }).join('')}`;
    
    // Find closest marker match using improved algorithm
    const extractedColor = { hex, rgb: { r, g, b } };
    const closestMarker = findClosestMarker(hex, availableMarkers);
    
    if (!closestMarker) {
      throw new Error('No markers available for matching');
    }
    
    const pickedColor = {
      id: Date.now() + Math.random(),
      position: { x, y },
      extractedColor,
      recommendedMarker: closestMarker,
      originalRecommendation: closestMarker, // Store original for reference
      timestamp: Date.now()
    };
    
    setPickedColors(prev => [...prev, pickedColor]);
    return pickedColor;
  };

  // Remove a picked color
  const removePickedColor = (colorId) => {
    setPickedColors(prev => prev.filter(color => color.id !== colorId));
  };

  // Replace recommended marker for a picked color
  const replaceRecommendedMarker = (colorId, newMarker) => {
    setPickedColors(prev => prev.map(color => 
      color.id === colorId 
        ? { ...color, recommendedMarker: newMarker }
        : color
    ));
  };

  // Handle cursor movement and color preview
  const handleCursorMove = (canvas, x, y, clientPosition) => {
    setCursorPosition({ x, y, ...clientPosition });
    
    // Preview color at cursor
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;
    const hex = `#${[r, g, b].map(x => {
      const hexValue = x.toString(16);
      return hexValue.length === 1 ? '0' + hexValue : hexValue;
    }).join('')}`;
    
    setHoveredColor({ hex, rgb: { r, g, b } });
  };

  // Clear all picked colors
  const clearPicking = () => {
    setPickedColors([]);
  };

  // Stop picking mode
  const stopPicking = () => {
    setIsPickingMode(false);
    setSelectedImage(null);
    setPickedColors([]);
    setHoveredColor(null);
  };

  // Create palette from picked colors
  const createPaletteFromPicks = () => {
    return pickedColors.map(pick => pick.recommendedMarker);
  };

  return {
    // State
    selectedImage,
    pickedColors,
    isPickingMode,
    cursorPosition,
    hoveredColor,
    
    // Actions
    startPicking,
    pickColorAt,
    removePickedColor,
    replaceRecommendedMarker,
    handleCursorMove,
    clearPicking,
    stopPicking,
    createPaletteFromPicks,
    
    // Legacy methods for compatibility
    updateCursorPosition: (x, y) => setCursorPosition({ x, y }),
    setColorHover: (color) => setHoveredColor(color),
    
    // Computed
    hasPickedColors: pickedColors.length > 0,
    pickedColorsCount: pickedColors.length
  };
}
